import { apiClient } from '../lib/api/client';
import type { NormalizedResume } from '../models/resume-draft';
import type { GenerationRules } from '../models/generation';
import type { Resume } from '../models/v2/profile';

// We can move these types to a models file later
export interface ResumeVersion {
    version_id: string;
    resume_id: string;
    version_number: number;
    resume_json: NormalizedResume;
    created_at: string;
    source: string;
    name?: string; // Dynamic name from backend
}

export interface GenerateResumeResponse {
    source_resume_id?: string;
    generated_resume_id: string; // The ID of the NEW AI resume
    version_id: string;
    resume_json: NormalizedResume;
    model_used: string;
    credits_charged: number;
}

export interface GeneratedResume {
    id: string;
    name: string;
    created_at: string;
    source_resume_id: string;
    job_description?: string;
}

export const resumeService = {
    _generatedResumesCache: null as GeneratedResume[] | null,

    getCachedGeneratedResumes: () => resumeService._generatedResumesCache,

    /**
     * Get history of generated resumes
     */
    getGeneratedResumes: async (): Promise<GeneratedResume[]> => {
        const data = await apiClient.get<GeneratedResume[]>('/api/resumes/generated');
        resumeService._generatedResumesCache = data;
        return data;
    },

    /**
     * Upload a new resume (PDF/DOCX)
     */
    upload: async (file: File, name?: string, isDefault?: boolean): Promise<Resume> => {
        const formData = new FormData();
        formData.append('file', file);
        if (name) formData.append('name', name);
        if (isDefault) formData.append('is_default', 'true');

        return apiClient.post<Resume>('/api/resumes', formData);
    },

    /**
     * Generates a new resume version from a job description.
     * This is the single entry point for creating "Drafts" (which are just v1 versions).
     */
    generateResume: async (
        jobInput: { description?: string, url?: string },
        resumeId: string | undefined, // This is the SOURCE resume ID (the upload)
        rules: GenerationRules
    ): Promise<GenerateResumeResponse> => {
        const payload: any = {
            resume_id: resumeId, // Optional: Triggers variant mode
            strictness: 'medium',
            model_preference: 'best',
            generation_rules: rules
        };

        if (jobInput.url) {
            payload.job_url = jobInput.url;
        } else {
            payload.job_description = jobInput.description;
        }

        return apiClient.post<GenerateResumeResponse>('/api/generate/resume', payload);
    },

    /**
     * Gets the latest version of a GENERATED resume.
     * Used by the Editor to load state.
     */
    getLatestVersion: async (generatedResumeId: string): Promise<ResumeVersion> => {
        // NOTE: New endpoint for generated resumes
        return apiClient.get<ResumeVersion>(`/api/resumes/generated/${generatedResumeId}/versions/latest`);
    },

    /**
     * Saves a new version of the GENERATED resume.
     */
    saveVersion: async (
        generatedResumeId: string,
        resumeJson: NormalizedResume,
        source: string = 'manual_edit'
    ): Promise<ResumeVersion> => {
        return apiClient.post<ResumeVersion>(`/api/resumes/generated/${generatedResumeId}/versions`, {
            resume_json: resumeJson,
            source
        });
    },

    /**
     * Finalizes the GENERATED resume version, downloads it, and cleans up history.
     */
    finalizeResume: async (generatedResumeId: string): Promise<{ pdf_url: string }> => {
        return apiClient.post<{ pdf_url: string }>(`/api/resumes/generated/${generatedResumeId}/finalize`);
    },

    /**
     * Deletes a resume (upload or generated) by ID.
     */
    deleteResume: async (resumeId: string): Promise<void> => {
        return apiClient.delete(`/api/resumes/${resumeId}`);
    },

    /**
     * Sets a resume as the default.
     */
    setDefault: async (resumeId: string): Promise<void> => {
        return apiClient.patch(`/api/resumes/${resumeId}/set-default`, {});
    }
};
